import React, { useContext, useState, useRef } from 'react';
import { useMutation } from '@apollo/client';
import {
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import get from 'lodash/get';

import UserContext from '../../../contexts/UserContext';
import { useSpinner } from '../../../contexts/SpinnerContext';
import Input from '../../../components/Input';
import { INTERNAL_SILENT_TOGGLE_DROPPOINT_STATUS } from '../query';
import { useCustomToast } from '../../../hooks/useCustomToast';

const ToggleDropPointStatusSchema = Yup.object().shape({
  comment: Yup.string().required('Du musst einen Kommentar/Begründung eingeben'),
});

interface ToggleDropPointStatusProps {
  stationId: string;
  isActive: boolean;
  refetch: () => void;
}

export const ToggleDropPointStatus = ({ stationId, isActive, refetch }: ToggleDropPointStatusProps) => {
  const [alertOpen, setAlertOpen] = useState(false);
  const onAlertClose = () => setAlertOpen(false);
  const cancelRef = useRef();

  const { user: internalUser } = useContext(UserContext);
  const { toggleSpinner } = useSpinner();
  const [toggleDropPointStatus] = useMutation(INTERNAL_SILENT_TOGGLE_DROPPOINT_STATUS);
  const toast = useCustomToast();

  const targetActive = !isActive;
  const buttonLabel = isActive ? 'DropPoint pausieren' : 'DropPoint aktivieren';
  const dialogTitle = isActive
    ? 'Möchtest du den DropPoint wirklich pausieren?'
    : 'Möchtest du den DropPoint wirklich aktivieren?';

  return (
    <>
      <Button
        variant="outline"
        colorScheme={isActive ? 'orange' : 'green'}
        size="xs"
        onClick={() => setAlertOpen(true)}>
        {buttonLabel}
      </Button>

      <AlertDialog isOpen={alertOpen} leastDestructiveRef={cancelRef} onClose={onAlertClose}>
        <AlertDialogOverlay />
        <AlertDialogContent>
          <Formik
            initialValues={{ comment: '' }}
            validationSchema={ToggleDropPointStatusSchema}
            onSubmit={async ({ comment }) => {
              try {
                toggleSpinner(true);
                onAlertClose();
                await toggleDropPointStatus({
                  variables: {
                    stationId,
                    active: targetActive,
                    internalUserId: internalUser.id,
                    comment,
                  },
                });
                await refetch();
                toast({
                  title: 'Erfolg!',
                  description: targetActive
                    ? 'Der DropPoint wurde erfolgreich aktiviert.'
                    : 'Der DropPoint wurde erfolgreich pausiert.',
                  status: 'success',
                });
              } catch (error) {
                console.log(error);
                toast({
                  description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
                  status: 'error',
                });
              } finally {
                toggleSpinner(false);
              }
            }}>
            <Form>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {dialogTitle}
              </AlertDialogHeader>

              <AlertDialogBody>
                <Input name="comment" label="Kommentar/Begründung" />
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onAlertClose}>
                  Abbrechen
                </Button>
                <Button colorScheme={targetActive ? 'green' : 'orange'} type="submit" ml={3}>
                  {buttonLabel}
                </Button>
              </AlertDialogFooter>
            </Form>
          </Formik>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
